import {
  AlertTriangle,
  ArrowRight,
  Backpack,
  MapPin,
  ShieldX,
  UsersRound,
} from "lucide-react";
import { useState } from "react";

import { formatDuration } from "../lib/format";
import { useModalFocus } from "../lib/useModalFocus";
import type { GameCommand, GameState } from "../types";
import { EventArtwork } from "./EventArtwork";

const priorities = [
  {
    id: "sustainment",
    name: "Food & water",
    detail: "Maximise the group's immediate survival.",
    perMark: "3% lower food and water needs",
  },
  {
    id: "medical",
    name: "Medical supplies",
    detail: "Protect the wounded during the journey.",
    perMark: "5% less severe injuries",
  },
  {
    id: "defence",
    name: "Weapons & ammunition",
    detail: "Arrive ready for whatever is waiting.",
    perMark: "+6 permanent defence",
  },
  {
    id: "rebuilding",
    name: "Tools & components",
    detail: "Recover faster once another shelter is found.",
    perMark: "4% faster bunker work",
  },
] as const;

export function EvacuationDialog({
  state,
  busy,
  onCommand,
}: {
  state: GameState;
  busy: boolean;
  onCommand: (command: GameCommand) => Promise<unknown>;
}) {
  const evacuation = state.evacuation;
  const [optionId, setOptionId] = useState(evacuation?.options[0]?.id ?? "");
  const [priority, setPriority] =
    useState<(typeof priorities)[number]["id"]>("sustainment");
  const modalRef = useModalFocus<HTMLDivElement>({
    focusKey: evacuation?.lost_site_name ?? "evacuation",
  });

  if (!evacuation) return null;

  const planned = evacuation.reason === "planned";
  const legacyAvailable =
    evacuation.legacy_mark_available ??
    (state.progress?.site_attacks_survived ?? 0) >= 3;
  const marks = state.legacy?.marks ?? {
    sustainment: 0,
    medical: 0,
    defence: 0,
    rebuilding: 0,
  };
  const selectedPriority =
    priorities.find((item) => item.id === priority) ?? priorities[0];
  const currentMark = Math.min(3, marks[priority]);
  const nextMark = Math.min(3, currentMark + 1);
  const markAtCap = currentMark >= 3;

  return (
    <div
      ref={modalRef}
      className="modal-backdrop evacuation-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="evacuation-title"
      aria-describedby="evacuation-description"
      aria-busy={busy}
      tabIndex={-1}
    >
      <section className="evacuation-dialog">
        <ShieldX className="evacuation-mark" aria-hidden="true" />
        <span className="eyebrow danger">
          {planned ? "Planned relocation" : "The bunker is lost"}
        </span>
        <h2 id="evacuation-title">Carry what matters. Leave the rest.</h2>
        <p id="evacuation-description">
          {planned
            ? "The community is leaving before the next assault can decide for them."
            : "The rooms and everything stored in them are gone."}{" "}
          There {evacuation.options.length === 1 ? "is" : "are"}{" "}
          {evacuation.options.length} uncertain refuge report
          {evacuation.options.length === 1 ? "" : "s"}.
        </p>
        {!planned && (
          <EventArtwork variant="overrun" caption="Visual reconstruction · evacuation route" />
        )}
        <div className="evacuation-survivors">
          <UsersRound aria-hidden="true" />
          <strong>
            {state.survivors.filter((item) => item.alive).length} survivors moving
          </strong>
          <span>{evacuation.lost_site_name} will be left behind.</span>
        </div>
        <div className="refuge-options" role="group" aria-label="Choose a reported refuge">
          {evacuation.options.map((option) => (
            <button
              type="button"
              aria-pressed={optionId === option.id}
              className={optionId === option.id ? "selected" : ""}
              onClick={() => setOptionId(option.id)}
              key={option.id}
            >
              <MapPin aria-hidden="true" />
              <span>
                <strong>{option.name}</strong>
                <small>{option.report}</small>
                <em>
                  {option.archetype_hint} · {option.geology_hint}
                </em>
              </span>
              <i>
                {option.confidence}% report confidence
                <br />
                {formatDuration(option.travel_seconds)} away
              </i>
            </button>
          ))}
        </div>
        <p className="carry-core-note">
          <Backpack aria-hidden="true" />A balanced survival go-bag is packed first. Your priority fills the remaining carry capacity.
        </p>
        <fieldset className="carry-priority">
          <legend>
            <Backpack aria-hidden="true" />
            {legacyAvailable ? "Remaining capacity · choose the legacy" : "Remaining capacity"}
          </legend>
          {priorities.map((item) => (
            <label className={priority === item.id ? "checked" : ""} key={item.id}>
              <input
                type="radio"
                name="carry-priority"
                value={item.id}
                checked={priority === item.id}
                onChange={() => setPriority(item.id)}
              />
              <strong>{item.name}</strong>
              <small>{item.detail}</small>
              {legacyAvailable && (
                <span className="legacy-mark-preview">
                  <em>
                    {Math.min(3, marks[item.id])}/3 marks
                    {marks[item.id] >= 3
                      ? " · maximum reached"
                      : ` → ${Math.min(3, marks[item.id] + 1)}/3 if chosen`}
                  </em>
                  <small>Each mark: {item.perMark}</small>
                </span>
              )}
            </label>
          ))}
        </fieldset>
        {legacyAvailable && (
          <aside
            className={`legacy-consequence ${markAtCap ? "capped" : ""}`}
            aria-label="Legacy consequence"
            aria-live="polite"
            aria-atomic="true"
          >
            <Backpack aria-hidden="true" />
            <span>
              <strong>
                {markAtCap
                  ? `${selectedPriority.name} legacy is already complete`
                  : `This departure earns one ${priority} legacy mark`}
              </strong>
              <small>
                {markAtCap
                  ? `The carry order still prioritises ${selectedPriority.name.toLowerCase()}, but this legacy remains at its 3-mark cap.`
                  : `${selectedPriority.name} rises from ${currentMark}/3 to ${nextMark}/3. Each mark grants ${selectedPriority.perMark}.`}
                {" "}Marks follow the community into every future bunker.
              </small>
            </span>
          </aside>
        )}
        <p className="blind-warning">
          <AlertTriangle aria-hidden="true" />
          The report may be wrong. {planned
            ? "Preparation improves the odds; it does not make the road safe."
            : "Running is still better than staying."}
        </p>
        <button
          type="button"
          className="danger-button"
          disabled={busy || !optionId}
          onClick={() =>
            onCommand({ type: "abandon_bunker", option_id: optionId, priority })
          }
        >
          {planned ? "Leave the bunker" : "Abandon the bunker"}
          <ArrowRight aria-hidden="true" />
        </button>
      </section>
    </div>
  );
}
